import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { savePurchaseOrder, getNextInvoiceNo } from "../../components/Api/GmsPurchaseOrder";
import { getPurchaseRequests, markPurchaseRequestAsReceived } from "../../components/Api/GmsPurchaseRequest";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { searchSuppliers } from "../../components/Api/GmsSupplier";
import { getProducts } from "../../components/Api/GmsProduct";
import { getActiveUnits } from "../../components/Api/GmsUnits";
import { getTaxes } from "../../components/Api/GmsTax";
import { getProductPreviousPrices } from "../../components/Api/GmsProductPrices";
import { Plus, Minus } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  hasPagePermissionWithAdmin,
  hasEditOrHigherPermission,
  isUserAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const defaultForm = {
  PurchaseOrder_Id: null,
  PurchaseOrder_InvoiceNo: "",
  PurchaseOrder_RefNo: "",
  PurchaseOrder_Supplier_Id: null,
  PurchaseOrder_InvoiceType_Id: 1,
  PurchaseOrder_Date: new Date(),
  PurchaseOrder_DueDate: new Date(),
  PurchaseOrder_Amount: 0,
  PurchaseOrder_DiscountPercent: 0,
  PurchaseOrder_DiscountAmount: 0,
  PurchaseOrder_OtherAmount: 0,
  PurchaseOrder_TaxAmount: 0,
  PurchaseOrder_OtherCharges: 0,
  PurchaseOrder_AdditionalDiscount: 0,
  PurchaseOrder_NetAmount: 0,
  PurchaseOrder_Notes: "",
  PurchaseOrder_TotalQty: 0,
  PurchaseOrder_Branch_Id: null,
  PurchaseOrder_TotalAmount: 0,
  PurchaseOrder_CreatedDate: new Date().toISOString(),
  PurchaseOrder_CreatedBy: 1,
  PurchaseOrder_Business_Id: null,
  Details: [],
};

const defaultDetail = {
  PurchaseOrderDet_Id: null,
  PurchaseOrderDet_Product_Id: null,
  PurchaseOrderDet_Unit_Id: 1,
  PurchaseOrderDet_Quantity: 0,
  PurchaseOrderDet_BonusQuantity: 0,
  PurchaseOrderDet_UnitPrice: 0,
  PurchaseOrderDet_DiscountPercent: 0,
  PurchaseOrderDet_DiscountAmount: 0,
  PurchaseOrderDet_TotalAmount: 0,
  PurchaseOrderDet_NetAmount: 0,
  PurchaseOrderDet_TaxPercent: 0,
  Product_NameEn: "",
  Unit_NameEn: "",
  TaxDetails: [],
};

const defaultStock = {
  batchQuantity: 0,
  PurchaseStock_BatchNo: "",
  PurchaseStock_ExpDate: null,
  PurchaseStock_SalesPrice: 0,
  PurchaseStock_CostPrice: 0,
  PurchaseStock_IsBonus: false,
};

const isValidDate = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') {
    return false;
  }
  const datePattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const match = dateStr.match(datePattern);
  if (!match) {
    return false;
  }
  const day = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);
  if (month < 1 || month > 12) {
    return false;
  }
  const daysInMonth = new Date(year, month, 0).getDate();
  if (day < 1 || day > daysInMonth) {
    return false;
  }
  if (year < 1900 || year > 2100) {
    return false;
  }
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return false;
  }
  return true;
};

const roundHalfUp = (value, decimals = 2) => {
  const factor = Math.pow(10, decimals);
  return Math.round((Number(value) + Number.EPSILON) * factor) / factor;
};

const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
  };
};

const formatNumericValue = (value) => {
  const numValue = parseFloat(value) || 0;
  return numValue === 0 ? '' : numValue;
};

export default function AddPurchasePage() {
  const navigate = useNavigate();

  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsAddPurchase');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsAddPurchase');
  const isAdmin = isUserAdmin();

  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [details, setDetails] = useState([{ ...defaultDetail }]);

  const selectedBranchObj = branches.find(b => b.branch_Id === form.PurchaseOrder_Branch_Id);
  const isPurchaseAllowed = selectedBranchObj ? selectedBranchObj.branch_AllowPurchase === true : false;

  const [saveLoading, setSaveLoading] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const lastGeneratedBusinessIdRef = useRef(null);

  // Supplier search states
  const [supplierSearchOptions, setSupplierSearchOptions] = useState([]);
  const [supplierSearchLoading, setSupplierSearchLoading] = useState(false);
  const [supplierSearchTimeout, setSupplierSearchTimeout] = useState(null);
  const [selectedSupplierOption, setSelectedSupplierOption] = useState(null);

  // Stock modal states
  const [showStockModal, setShowStockModal] = useState(false);
  const [currentDetailIndex, setCurrentDetailIndex] = useState(0);
  const [stockDetails, setStockDetails] = useState([]);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [previousPrices, setPreviousPrices] = useState(null);

  // Purchase Order selection modal states
  const [showPurchaseOrderModal, setShowPurchaseOrderModal] = useState(false);
  const [purchaseOrdersList, setPurchaseOrdersList] = useState([]);
  const [loadingPurchaseOrders, setLoadingPurchaseOrders] = useState(false);
  const [selectedPurchaseOrderId, setSelectedPurchaseOrderId] = useState(null);
  const [selectedPurchaseOrderData, setSelectedPurchaseOrderData] = useState(null);
  const [markAsReceived, setMarkAsReceived] = useState(false);
  const [originalPurchaseOrderQuantities, setOriginalPurchaseOrderQuantities] = useState({});

  useEffect(() => {
    if (hasPermission && hasEditOrHigher) {
      loadBusinesses();
      loadBranches();
      loadProducts();
      loadUnits();
      loadTaxes();
    } else if (!hasPermission) {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, hasEditOrHigher]);

  // Auto-populate company if user has companyId
  useEffect(() => {
    const userCompanyId = localStorage.getItem("userCompanyId");
    if (userCompanyId && businesses.length > 0) {
      const companyExists = businesses.find(b => b.business_Id === parseInt(userCompanyId));
      if (companyExists && !form.PurchaseOrder_Business_Id) {
        setForm(prev => ({
          ...prev,
          PurchaseOrder_Business_Id: parseInt(userCompanyId)
        }));
      }
    }
  }, [businesses]);

  // Auto-populate branch if user has branchId
  useEffect(() => {
    const userBranchId = localStorage.getItem("userBranchId");
    if (userBranchId && branches.length > 0 && form.PurchaseOrder_Business_Id) {
      const branchExists = branches.find(b => b.branch_Id === parseInt(userBranchId) && b.branch_Buisness_Id === form.PurchaseOrder_Business_Id);
      if (branchExists && !form.PurchaseOrder_Branch_Id) {
        setForm(prev => ({
          ...prev,
          PurchaseOrder_Branch_Id: parseInt(userBranchId)
        }));
      }
    }
  }, [branches, form.PurchaseOrder_Business_Id]);

  // Auto-generate invoice number when business is selected
  useEffect(() => {
    if (
      form.PurchaseOrder_Business_Id &&
      !form.PurchaseOrder_InvoiceNo &&
      lastGeneratedBusinessIdRef.current !== form.PurchaseOrder_Business_Id
    ) {
      const generateInvoiceNo = async () => {
        try {
          lastGeneratedBusinessIdRef.current = form.PurchaseOrder_Business_Id;
          const result = await getNextInvoiceNo(form.PurchaseOrder_Business_Id, false, null);
          if (result.nextInvoiceNo) {
            setForm(prev => ({
              ...prev,
              PurchaseOrder_InvoiceNo: result.nextInvoiceNo
            }));
          }
        } catch (error) {
          console.error("Error getting next invoice number:", error);
          lastGeneratedBusinessIdRef.current = null;
        }
      };
      generateInvoiceNo();
    }
  }, [form.PurchaseOrder_Business_Id, form.PurchaseOrder_InvoiceNo]);

  const loadBusinesses = async () => {
    try {
      const data = await getBusinesses({ business_Active: true });
      const businessData = Array.isArray(data) ? data : (data.data || []);
      setBusinesses(businessData);
    } catch (error) {
      console.error("Error loading businesses:", error);
      Swal.fire("Error", "Failed to load companies", "error");
    }
  };

  const loadBranches = async () => {
    try {
      const data = await getBranches({ branch_Active: true });
      setBranches(data || []);
    } catch (error) {
      console.error("Error loading branches:", error);
      Swal.fire("Error", "Failed to load branches", "error");
    }
  };

  const loadProducts = async () => {
    try {
      const data = await getProducts({});
      setProducts(data || []);
    } catch (error) {
      console.error("Error loading products:", error);
      Swal.fire("Error", "Failed to load products", "error");
    }
  };

  const loadUnits = async () => {
    try {
      const data = await getActiveUnits();
      setUnits(data || []);
    } catch (error) {
      console.error("Error loading units:", error);
      Swal.fire("Error", "Failed to load units", "error");
    }
  };

  const loadTaxes = async () => {
    try {
      const data = await getTaxes({ tax_Active: true });
      setTaxes(data || []);
    } catch (error) {
      console.error("Error loading taxes:", error);
      Swal.fire("Error", "Failed to load taxes", "error");
    }
  };

  const searchSuppliersManually = async (inputValue) => {
    const trimmedInput = inputValue ? inputValue.trim() : '';
    if (!trimmedInput || trimmedInput.length < 3) {
      setSupplierSearchOptions([]);
      return;
    }
    try {
      setSupplierSearchLoading(true);
      const response = await searchSuppliers(trimmedInput, 1, 20);
      if (!response || !response.items || !Array.isArray(response.items)) {
        setSupplierSearchOptions([]);
        return;
      }
      const formattedOptions = response.items.map(supplier => {
        const hasArabicName = supplier.supplier_NameAr && supplier.supplier_NameAr.trim() !== '';
        const hasEnglishName = supplier.supplier_NameEn && supplier.supplier_NameEn.trim() !== '';
        const containsArabicChars = /[\u0600-\u06FF]/.test(trimmedInput);
        const searchInArabic = hasArabicName && (
          supplier.supplier_NameAr.includes(trimmedInput) ||
          (containsArabicChars && supplier.supplier_NameAr.includes(trimmedInput))
        );
        let primaryName, secondaryName;
        if (searchInArabic && hasArabicName) {
          primaryName = supplier.supplier_NameAr;
          secondaryName = hasEnglishName ? supplier.supplier_NameEn : null;
        } else if (hasEnglishName) {
          primaryName = supplier.supplier_NameEn;
          secondaryName = hasArabicName ? supplier.supplier_NameAr : null;
        } else if (hasArabicName) {
          primaryName = supplier.supplier_NameAr;
          secondaryName = null;
        } else {
          primaryName = 'N/A';
          secondaryName = null;
        }
        let label = primaryName;
        if (secondaryName && secondaryName.trim() !== '') {
          label = `${primaryName} — ${secondaryName}`;
        }
        label += ` — ${supplier.supplier_Code || supplier.supplier_ContactNo || 'N/A'}`;
        return {
          value: supplier.supplier_Id,
          label: label,
          supplier: supplier
        };
      });
      setSupplierSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching suppliers:', error);
      setSupplierSearchOptions([]);
    } finally {
      setSupplierSearchLoading(false);
    }
  };

  const handleBusinessChange = async (businessId) => {
    setForm(prev => ({
      ...prev,
      PurchaseOrder_Business_Id: businessId,
      PurchaseOrder_Branch_Id: null
    }));
    if (businessId) {
      try {
        const result = await getNextInvoiceNo(businessId, false, null);
        if (result.nextInvoiceNo) {
          setForm(prev => ({
            ...prev,
            PurchaseOrder_InvoiceNo: result.nextInvoiceNo
          }));
        }
      } catch (error) {
        console.error("Error getting next invoice number:", error);
      }
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getDetailField = (detail, fieldName) => {
    if (fieldName === 'Product_NameEn') {
      return detail.Product_NameEn || detail.product_NameEn || '';
    }
    if (fieldName === 'Unit_NameEn') {
      return detail.Unit_NameEn || detail.unit_NameEn || '';
    }
    if (detail[fieldName] !== undefined) {
      return detail[fieldName];
    }
    const lowercaseFieldName = fieldName.replace('PurchaseOrderDet_', 'purchaseOrderDet_');
    if (detail[lowercaseFieldName] !== undefined) {
      return detail[lowercaseFieldName];
    }
    return '';
  };

  const handleDetailChange = async (index, field, value) => {
    const newDetails = [...details];
    newDetails[index] = { ...newDetails[index], [field]: value };

    if (field === 'PurchaseOrderDet_Product_Id' && value) {
      const product = products.find(p => p.product_Id === value);
      if (product) {
        const unit = units.find(u => u.value === product.unit_Id);
        const unitName = unit ? unit.label.split(' - ')[1] || unit.label : "Unit";
        let taxPercent = 0;
        let taxDetails = [];
        if (product.taxIds && product.taxIds.length > 0) {
          const productTaxes = taxes.filter(tax => product.taxIds.includes(tax.tax_Id));
          taxPercent = productTaxes.reduce((sum, tax) => sum + (tax.tax_Perc || 0), 0);
          taxDetails = productTaxes.map(tax => ({
            PurchaseOrderTaxDet_Tax_Id: tax.tax_Id,
            Tax_NameEn: tax.tax_NameEn,
            Tax_Perc: tax.tax_Perc
          }));
        }
        let previousUnitPrice = product.product_CostPrice || 0;
        try {
          const prices = await getProductPreviousPrices(value);
          if (prices?.previousUnitPrice) {
            previousUnitPrice = prices.previousUnitPrice;
          }
        } catch (error) {
          console.error('Error fetching previous unit price:', error);
        }
        newDetails[index] = {
          ...newDetails[index],
          PurchaseOrderDet_UnitPrice: previousUnitPrice,
          PurchaseOrderDet_TaxPercent: taxPercent,
          PurchaseOrderDet_Unit_Id: product.unit_Id || 1,
          Product_NameEn: product.product_NameEn,
          Unit_NameEn: unitName,
          expiryRequired: product.expiryRequired || false,
          TaxDetails: taxDetails
        };
      }
    }

    if (field === 'PurchaseOrderDet_Quantity' || field === 'PurchaseOrderDet_BonusQuantity' ||
      field === 'PurchaseOrderDet_UnitPrice' || field === 'PurchaseOrderDet_DiscountPercent' ||
      field === 'PurchaseOrderDet_TaxPercent') {
      calculateDetailAmounts(newDetails, index);
      recalculateTotalsFromDetails(newDetails);
    }
    setDetails(newDetails);
  };

  const calculateDetailAmounts = (detailsArray, index) => {
    const detail = detailsArray[index];
    const quantity = parseFloat(detail.PurchaseOrderDet_Quantity) || 0;
    const bonusQuantity = parseFloat(detail.PurchaseOrderDet_BonusQuantity) || 0;
    const unitPrice = parseFloat(detail.PurchaseOrderDet_UnitPrice) || 0;
    const discountPercent = parseFloat(detail.PurchaseOrderDet_DiscountPercent) || 0;
    const taxPercent = parseFloat(detail.PurchaseOrderDet_TaxPercent) || 0;

    if (quantity <= 0) {
      detailsArray[index] = {
        ...detail,
        PurchaseOrderDet_DiscountAmount: 0,
        PurchaseOrderDet_TotalAmount: 0,
        PurchaseOrderDet_TaxAmount: 0,
        PurchaseOrderDet_NetAmount: 0
      };
      return;
    }

    const amountBeforeDiscount = roundHalfUp(quantity * unitPrice, 2);
    const discountedUnitPrice = roundHalfUp(unitPrice * (1 - (discountPercent / 100)), 2);
    const discountAmount = roundHalfUp(quantity * unitPrice * (discountPercent / 100), 2);
    const lineTotalAmount = roundHalfUp(quantity * discountedUnitPrice, 2);
    const totalQuantity = quantity + bonusQuantity;
    const taxBase = totalQuantity * discountedUnitPrice;
    let taxComponentsSum = 0;
    if (Array.isArray(detail.TaxDetails) && detail.TaxDetails.length > 0) {
      taxComponentsSum = detail.TaxDetails.reduce((sum, t) => {
        const perc = parseFloat(t.Tax_Perc || t.tax_Perc || 0) || 0;
        const comp = roundHalfUp(taxBase * (perc / 100), 2);
        return sum + comp;
      }, 0);
    } else {
      taxComponentsSum = roundHalfUp(taxBase * (taxPercent / 100), 2);
    }
    const taxAmount = roundHalfUp(taxComponentsSum, 2);
    const netAmount = roundHalfUp(lineTotalAmount + taxAmount, 2);

    detailsArray[index] = {
      ...detail,
      PurchaseOrderDet_DiscountAmount: discountAmount,
      PurchaseOrderDet_TotalAmount: amountBeforeDiscount,
      PurchaseOrderDet_TaxAmount: taxAmount,
      PurchaseOrderDet_NetAmount: netAmount
    };
  };

  const recalculateTotalsFromDetails = (detailsArray) => {
    let totalAmount = 0;
    let totalDiscount = 0;
    let totalTax = 0;
    let totalQty = 0;

    detailsArray.forEach((detail) => {
      if (detail && (detail.PurchaseOrderDet_Product_Id || detail.purchaseOrderDet_Product_Id)) {
        const totalAmountValue = parseFloat(detail.PurchaseOrderDet_TotalAmount || detail.purchaseOrderDet_TotalAmount) || 0;
        const discountAmount = parseFloat(detail.PurchaseOrderDet_DiscountAmount || detail.purchaseOrderDet_DiscountAmount) || 0;
        const taxAmount = parseFloat(detail.PurchaseOrderDet_TaxAmount || detail.purchaseOrderDet_TaxAmount) || 0;
        const quantity = parseFloat(detail.PurchaseOrderDet_Quantity || detail.purchaseOrderDet_Quantity) || 0;
        const bonusQuantity = parseFloat(detail.PurchaseOrderDet_BonusQuantity || detail.purchaseOrderDet_BonusQuantity) || 0;

        totalAmount += totalAmountValue;
        totalDiscount += discountAmount;
        totalTax += taxAmount;
        totalQty += (quantity + bonusQuantity);
      }
    });

    totalAmount = roundHalfUp(totalAmount, 2);
    totalDiscount = roundHalfUp(totalDiscount, 2);
    totalTax = roundHalfUp(totalTax, 2);
    totalQty = roundHalfUp(totalQty, 4);

    const otherCharges = parseFloat(form.PurchaseOrder_OtherCharges) || 0;
    const additionalDiscount = parseFloat(form.PurchaseOrder_AdditionalDiscount) || 0;
    const amountAfterDiscount = totalAmount - totalDiscount;
    const netAmount = roundHalfUp(amountAfterDiscount + totalTax + otherCharges - additionalDiscount, 2);

    setForm(prev => ({
      ...prev,
      PurchaseOrder_TotalAmount: totalAmount,
      PurchaseOrder_DiscountAmount: totalDiscount,
      PurchaseOrder_TaxAmount: totalTax,
      PurchaseOrder_OtherCharges: otherCharges,
      PurchaseOrder_AdditionalDiscount: additionalDiscount,
      PurchaseOrder_NetAmount: netAmount,
      PurchaseOrder_TotalQty: totalQty
    }));
  };

  useEffect(() => {
    recalculateTotalsFromDetails(details);
  }, [details, form.PurchaseOrder_OtherCharges, form.PurchaseOrder_AdditionalDiscount]);

  const addDetail = () => {
    for (let i = 0; i < details.length; i++) {
      const detail = details[i];
      if (detail.PurchaseOrderDet_Product_Id) {
        if (!detail.stockDetails || detail.stockDetails.length === 0) {
          Swal.fire({
            icon: 'error',
            title: 'Stock Details Required',
            text: `Please enter stock details (including sales price) for product "${detail.Product_NameEn || 'Unknown'}" before adding a new row.`,
            confirmButtonText: 'OK'
          }).then(() => {
            openStockModal(i);
          });
          return;
        }
        const missingSalesPrice = detail.stockDetails.find((stock) => {
          const salesPrice = parseFloat(stock.PurchaseStock_SalesPrice || stock.purchaseStock_SalesPrice) || 0;
          return salesPrice <= 0;
        });
        if (missingSalesPrice) {
          Swal.fire({
            icon: 'error',
            title: 'Sales Price Required',
            text: `Please enter a sales price greater than 0 for all stock items of product "${detail.Product_NameEn || 'Unknown'}" before adding a new row.`,
            confirmButtonText: 'OK'
          }).then(() => {
            openStockModal(i);
          });
          return;
        }
      }
    }
    setDetails([...details, { ...defaultDetail }]);
  };

  const removeDetail = (index) => {
    if (details.length > 1) {
      const newDetails = details.filter((_, i) => i !== index);
      setDetails(newDetails);
      recalculateTotalsFromDetails(newDetails);
    }
  };

  const openStockModal = async (index) => {
    const detail = details[index];
    const productId = getDetailField(detail, 'PurchaseOrderDet_Product_Id');
    if (!detail || !productId) {
      Swal.fire("Error", "Please select a product first", "error");
      return;
    }
    const product = products.find(p => p.product_Id === productId);
    setCurrentProduct(product);
    setCurrentDetailIndex(index);

    try {
      const prices = await getProductPreviousPrices(productId);
      setPreviousPrices(prices);
    } catch (error) {
      console.error('Error fetching previous prices:', error);
      setPreviousPrices(null);
    }

    if (detail.stockDetails && detail.stockDetails.length > 0) {
      const firstItem = detail.stockDetails[0];
      const hasBatchQuantity = firstItem && (
        (firstItem.batchQuantity !== undefined && firstItem.batchQuantity !== null) ||
        (firstItem.PurchaseStock_BatchQuantity !== undefined && firstItem.PurchaseStock_BatchQuantity !== null)
      );
      if (hasBatchQuantity) {
        setStockDetails(detail.stockDetails);
      } else {
        const batches = [];
        const stockMap = new Map();
        detail.stockDetails.forEach(stock => {
          const isBonus = stock.PurchaseStock_IsBonus || stock.purchaseStock_IsBonus || false;
          const key = `${stock.PurchaseStock_BatchNo || stock.purchaseStock_BatchNo || ''}_${stock.PurchaseStock_ExpDate || stock.purchaseStock_ExpDate || ''}_${stock.PurchaseStock_SalesPrice || stock.purchaseStock_SalesPrice || 0}_${isBonus}`;
          if (stockMap.has(key)) {
            stockMap.get(key).batchQuantity += 1;
            stockMap.get(key).PurchaseStock_BatchQuantity = stockMap.get(key).batchQuantity;
          } else {
            stockMap.set(key, {
              batchQuantity: 1,
              PurchaseStock_BatchQuantity: 1,
              PurchaseStock_BatchNo: stock.PurchaseStock_BatchNo || stock.purchaseStock_BatchNo || '',
              PurchaseStock_ExpDate: stock.PurchaseStock_ExpDate || stock.purchaseStock_ExpDate || null,
              PurchaseStock_SalesPrice: stock.PurchaseStock_SalesPrice || stock.purchaseStock_SalesPrice || 0,
              PurchaseStock_CostPrice: stock.PurchaseStock_CostPrice || stock.purchaseStock_CostPrice || 0,
              PurchaseStock_IsBonus: isBonus,
              purchaseStock_IsBonus: isBonus,
            });
          }
        });
        batches.push(...Array.from(stockMap.values()));
        setStockDetails(batches);
      }
    } else {
      const quantity = parseFloat(detail.purchaseOrderDet_Quantity || detail.PurchaseOrderDet_Quantity) || 0;
      const bonusQuantity = parseFloat(detail.purchaseOrderDet_BonusQuantity || detail.PurchaseOrderDet_BonusQuantity) || 0;
      const stockDetailsArray = [];
      if (quantity > 0) {
        stockDetailsArray.push({
          ...defaultStock,
          batchQuantity: quantity,
          PurchaseStock_IsBonus: false
        });
      }
      if (bonusQuantity > 0) {
        stockDetailsArray.push({
          ...defaultStock,
          batchQuantity: bonusQuantity,
          PurchaseStock_IsBonus: true
        });
      }
      if (stockDetailsArray.length === 0) {
        stockDetailsArray.push({
          ...defaultStock,
          batchQuantity: 0,
          PurchaseStock_IsBonus: false
        });
      }
      setStockDetails(stockDetailsArray);
    }
    setShowStockModal(true);
  };

  const handleStockChange = (index, field, value) => {
    const newStockDetails = [...stockDetails];
    if (field === 'PurchaseStock_ExpDate' && value) {
      let formattedValue = value.replace(/\D/g, '');
      if (formattedValue.length >= 2) {
        formattedValue = formattedValue.substring(0, 2) + '/' + formattedValue.substring(2);
      }
      if (formattedValue.length >= 5) {
        formattedValue = formattedValue.substring(0, 5) + '/' + formattedValue.substring(5, 9);
      }
      newStockDetails[index] = { ...newStockDetails[index], [field]: formattedValue };
    } else if (field === 'batchQuantity') {
      let numValue = parseFloat(value) || 0;
      const currentBatch = newStockDetails[index];
      const currentStockIds = currentBatch.stockIds || [];
      newStockDetails[index] = {
        ...currentBatch,
        batchQuantity: numValue,
        stockIds: currentStockIds
      };
    } else {
      newStockDetails[index] = { ...newStockDetails[index], [field]: value };
    }

    if (field === 'PurchaseStock_BatchNo' || field === 'PurchaseStock_ExpDate' || field === 'PurchaseStock_SalesPrice') {
      for (let i = index + 1; i < newStockDetails.length; i++) {
        newStockDetails[i] = { ...newStockDetails[i], [field]: newStockDetails[index][field] };
      }
    }
    setStockDetails(newStockDetails);
  };

  const addNextBatch = (isBonus = false) => {
    const detail = details[currentDetailIndex];
    if (!detail) return;
    const quantity = parseFloat(detail.purchaseOrderDet_Quantity || detail.PurchaseOrderDet_Quantity) || 0;
    const bonusQuantity = parseFloat(detail.purchaseOrderDet_BonusQuantity || detail.PurchaseOrderDet_BonusQuantity) || 0;

    setStockDetails(currentStockDetails => {
      const allocatedQuantity = currentStockDetails
        .filter(batch => {
          const batchIsBonus = batch.PurchaseStock_IsBonus || batch.purchaseStock_IsBonus || false;
          return batchIsBonus === isBonus;
        })
        .reduce((sum, batch) => {
          const batchQty = parseFloat(batch.batchQuantity) || 0;
          return sum + batchQty;
        }, 0);
      const targetQuantity = isBonus ? bonusQuantity : quantity;
      const remainingQuantity = targetQuantity - allocatedQuantity;
      if (remainingQuantity <= 0) {
        Swal.fire({
          icon: 'info',
          title: 'No Remaining Quantity',
          text: `All ${isBonus ? 'bonus' : 'regular'} quantity has been allocated to batches.`,
          confirmButtonText: 'OK'
        });
        return currentStockDetails;
      }
      const lastBatchOfType = [...currentStockDetails]
        .reverse()
        .find(batch => {
          const batchIsBonus = batch.PurchaseStock_IsBonus || batch.purchaseStock_IsBonus || false;
          return batchIsBonus === isBonus;
        }) || {};
      const newBatch = {
        ...defaultStock,
        batchQuantity: remainingQuantity,
        PurchaseStock_BatchNo: lastBatchOfType.PurchaseStock_BatchNo || '',
        PurchaseStock_ExpDate: lastBatchOfType.PurchaseStock_ExpDate || null,
        PurchaseStock_SalesPrice: lastBatchOfType.PurchaseStock_SalesPrice || 0,
        PurchaseStock_CostPrice: lastBatchOfType.PurchaseStock_CostPrice || 0,
        PurchaseStock_IsBonus: isBonus,
      };
      return [...currentStockDetails, newBatch];
    });
  };

  const saveStockDetails = () => {
    const detail = details[currentDetailIndex];
    if (!detail) {
      return;
    }
    const quantity = parseFloat(detail.purchaseOrderDet_Quantity || detail.PurchaseOrderDet_Quantity) || 0;
    const bonusQuantity = parseFloat(detail.purchaseOrderDet_BonusQuantity || detail.PurchaseOrderDet_BonusQuantity) || 0;
    const regularBatchQty = stockDetails
      .filter(batch => !(batch.PurchaseStock_IsBonus || batch.purchaseStock_IsBonus))
      .reduce((sum, batch) => sum + (parseFloat(batch.batchQuantity || batch.PurchaseStock_BatchQuantity) || 0), 0);
    const bonusBatchQty = stockDetails
      .filter(batch => (batch.PurchaseStock_IsBonus || batch.purchaseStock_IsBonus))
      .reduce((sum, batch) => sum + (parseFloat(batch.batchQuantity || batch.PurchaseStock_BatchQuantity) || 0), 0);

    if (regularBatchQty !== quantity) {
      Swal.fire({
        icon: 'error',
        title: 'Regular Quantity Mismatch',
        text: `Total regular batch quantity (${regularBatchQty}) must equal regular quantity (${quantity}). Please adjust the regular batch quantities.`,
        confirmButtonText: 'OK'
      });
      return;
    }
    if (bonusBatchQty !== bonusQuantity) {
      Swal.fire({
        icon: 'error',
        title: 'Bonus Quantity Mismatch',
        text: `Total bonus batch quantity (${bonusBatchQty}) must equal bonus quantity (${bonusQuantity}). Please adjust the bonus batch quantities.`,
        confirmButtonText: 'OK'
      });
      return;
    }

    const invalidBatch = stockDetails.find((batch) => {
      const batchQty = parseFloat(batch.batchQuantity || batch.PurchaseStock_BatchQuantity) || 0;
      return batchQty <= 0;
    });
    if (invalidBatch) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Batch Quantity',
        text: `All batches must have a quantity greater than 0.`,
        confirmButtonText: 'OK'
      });
      return;
    }

    const productExpiryRequired = currentProduct?.expiryRequired || detail?.expiryRequired || false;
    if (productExpiryRequired) {
      const missingExpiryDate = stockDetails.find((batch) => {
        const expDate = batch.PurchaseStock_ExpDate || batch.purchaseStock_ExpDate || '';
        return !expDate || expDate.trim() === '';
      });
      if (missingExpiryDate) {
        Swal.fire({
          icon: 'error',
          title: 'Expiry Date Required',
          text: `Please enter an expiry date for all batches. This product requires an expiry date.`,
          confirmButtonText: 'OK'
        });
        return;
      }
      const invalidExpiryDate = stockDetails.find((batch) => {
        const expDate = batch.PurchaseStock_ExpDate || batch.purchaseStock_ExpDate || '';
        if (!expDate || expDate.trim() === '') return false;
        return !isValidDate(expDate);
      });
      if (invalidExpiryDate) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid Expiry Date',
          text: `Please enter a valid expiry date in DD/MM/YYYY format for all batches.`,
          confirmButtonText: 'OK'
        });
        return;
      }
    }

    // Format stock details properly with PurchaseStock_BatchQuantity
    const formattedStockDetails = stockDetails.map(stock => ({
      ...stock,
      PurchaseStock_BatchQuantity: stock.PurchaseStock_BatchQuantity || stock.batchQuantity || 0,
      batchQuantity: stock.batchQuantity || stock.PurchaseStock_BatchQuantity || 0 // Keep both for compatibility
    }));

    const newDetails = [...details];
    newDetails[currentDetailIndex] = {
      ...newDetails[currentDetailIndex],
      stockDetails: formattedStockDetails
    };
    setDetails(newDetails);
    setShowStockModal(false);
  };

  const handleStockModalClose = () => {
    const detail = details[currentDetailIndex];
    if (detail && stockDetails && stockDetails.length > 0) {
      const newDetails = [...details];
      newDetails[currentDetailIndex] = {
        ...newDetails[currentDetailIndex],
        stockDetails: stockDetails,
      };
      setDetails(newDetails);
    }
    setShowStockModal(false);
  };

  const loadPurchaseOrdersForSupplier = async (supplierId) => {
    if (!supplierId) {
      setPurchaseOrdersList([]);
      return;
    }
    try {
      setLoadingPurchaseOrders(true);
      const filter = {
        PurchaseRequest_Supplier_Id: supplierId,
        PurchaseRequest_Is_Received: false
      };
      const data = await getPurchaseRequests(filter);
      setPurchaseOrdersList(data || []);
      if (data && data.length > 0) {
        setShowPurchaseOrderModal(true);
      }
    } catch (error) {
      console.error("Error loading purchase orders for supplier:", error);
      setPurchaseOrdersList([]);
    } finally {
      setLoadingPurchaseOrders(false);
    }
  };

  const handlePurchaseOrderSelect = async (purchaseOrder) => {
    try {
      setLoadingPurchaseOrders(true);
      const poId = purchaseOrder.purchaseRequest_Id || purchaseOrder.PurchaseRequest_Id;
      setSelectedPurchaseOrderId(poId);
      setSelectedPurchaseOrderData(purchaseOrder);

      setForm(prev => ({
        ...prev,
        PurchaseOrder_RefNo: purchaseOrder.purchaseRequest_RefNo || purchaseOrder.PurchaseRequest_RefNo || "",
        PurchaseOrder_Supplier_Id: purchaseOrder.purchaseRequest_Supplier_Id || purchaseOrder.PurchaseRequest_Supplier_Id,
        PurchaseOrder_InvoiceType_Id: purchaseOrder.purchaseRequest_InvoiceType_Id || purchaseOrder.PurchaseRequest_InvoiceType_Id || 1,
        PurchaseOrder_Date: (purchaseOrder.purchaseRequest_Date || purchaseOrder.PurchaseRequest_Date) ? new Date(purchaseOrder.purchaseRequest_Date || purchaseOrder.PurchaseRequest_Date) : new Date(),
        PurchaseOrder_DueDate: (purchaseOrder.purchaseRequest_DueDate || purchaseOrder.PurchaseRequest_DueDate) ? new Date(purchaseOrder.purchaseRequest_DueDate || purchaseOrder.PurchaseRequest_DueDate) : new Date(),
        PurchaseOrder_Branch_Id: purchaseOrder.purchaseRequest_Branch_Id || purchaseOrder.PurchaseRequest_Branch_Id,
        PurchaseOrder_Business_Id: purchaseOrder.purchaseRequest_Business_Id || purchaseOrder.PurchaseRequest_Business_Id,
        PurchaseOrder_Notes: purchaseOrder.purchaseRequest_Notes || purchaseOrder.PurchaseRequest_Notes || "",
        PurchaseOrder_TotalAmount: purchaseOrder.purchaseRequest_TotalAmount || purchaseOrder.PurchaseRequest_TotalAmount || 0,
        PurchaseOrder_DiscountAmount: purchaseOrder.purchaseRequest_DiscountAmount || purchaseOrder.PurchaseRequest_DiscountAmount || 0,
        PurchaseOrder_TaxAmount: purchaseOrder.purchaseRequest_TaxAmount || purchaseOrder.PurchaseRequest_TaxAmount || 0,
        PurchaseOrder_NetAmount: purchaseOrder.purchaseRequest_NetAmount || purchaseOrder.PurchaseRequest_NetAmount || 0,
        PurchaseOrder_Amount: purchaseOrder.purchaseRequest_Amount || purchaseOrder.PurchaseRequest_Amount || 0,
        PurchaseOrder_DiscountPercent: purchaseOrder.purchaseRequest_DiscountPercent || purchaseOrder.PurchaseRequest_DiscountPercent || 0,
        PurchaseOrder_OtherAmount: purchaseOrder.purchaseRequest_OtherAmount || purchaseOrder.PurchaseRequest_OtherAmount || 0,
      }));

      const supplierId = purchaseOrder.purchaseRequest_Supplier_Id || purchaseOrder.PurchaseRequest_Supplier_Id;
      const supplierName = purchaseOrder.supplier_NameEn || purchaseOrder.Supplier_NameEn;
      if (supplierId && supplierName) {
        const supplier = await searchSuppliers(supplierName, 1, 1);
        if (supplier?.items?.[0]) {
          const s = supplier.items[0];
          const supplierCode = s.supplier_Code || purchaseOrder.supplier_Code || purchaseOrder.supplier_ContactNo || '';
          setSelectedSupplierOption({
            value: supplierId,
            label: `${supplierName} — ${supplierCode}`
          });
        }
      }

      const businessId = purchaseOrder.purchaseRequest_Business_Id || purchaseOrder.PurchaseRequest_Business_Id;
      if (businessId) {
        try {
          const result = await getNextInvoiceNo(businessId, false, null);
          if (result.nextInvoiceNo) {
            setForm(prev => ({
              ...prev,
              PurchaseOrder_InvoiceNo: result.nextInvoiceNo
            }));
          }
        } catch (error) {
          console.error("Error getting next invoice number:", error);
        }
      }

      if (purchaseOrder.details && purchaseOrder.details.length > 0) {
        const mappedDetails = purchaseOrder.details.map((detail) => {
          const qty = detail.purchaseRequestDet_Quantity || detail.PurchaseRequestDet_Quantity || 0;
          const bonusQty = detail.purchaseRequestDet_BonusQuantity || detail.PurchaseRequestDet_BonusQuantity || 0;
          const productId = detail.purchaseRequestDet_Product_Id || detail.PurchaseRequestDet_Product_Id;
          const unitId = detail.purchaseRequestDet_Unit_Id || detail.PurchaseRequestDet_Unit_Id;
          const unitPrice = detail.purchaseRequestDet_UnitPrice || detail.PurchaseRequestDet_UnitPrice || 0;
          const discountPercent = detail.purchaseRequestDet_DiscountPercent || detail.PurchaseRequestDet_DiscountPercent || 0;
          const taxPercent = detail.purchaseRequestDet_TaxPercent || detail.PurchaseRequestDet_TaxPercent || 0;
          const productName = detail.product_NameEn || detail.Product_NameEn;
          const unitName = detail.unit_NameEn || detail.Unit_NameEn;
          const taxDetailsRaw = detail.taxDetails || detail.TaxDetails || [];
          const taxDetails = taxDetailsRaw.map(tax => ({
            PurchaseOrderTaxDet_Tax_Id: tax.purchaseRequestTaxDet_Tax_Id || tax.PurchaseRequestTaxDet_Tax_Id || tax.tax_Id || tax.Tax_Id,
            Tax_NameEn: tax.tax_NameEn || tax.Tax_NameEn,
            Tax_Perc: tax.tax_Perc || tax.Tax_Perc,
          }));
          const originalQty = qty;
          const originalBonusQty = bonusQty;
          setOriginalPurchaseOrderQuantities(prev => ({
            ...prev,
            [productId]: { originalQty, originalBonusQty }
          }));
          return {
            PurchaseOrderDet_Id: null,
            PurchaseOrderDet_Product_Id: productId,
            PurchaseOrderDet_Unit_Id: unitId,
            PurchaseOrderDet_Quantity: qty,
            PurchaseOrderDet_BonusQuantity: bonusQty,
            PurchaseOrderDet_UnitPrice: unitPrice,
            PurchaseOrderDet_DiscountPercent: discountPercent,
            PurchaseOrderDet_DiscountAmount: 0,
            PurchaseOrderDet_TotalAmount: 0,
            PurchaseOrderDet_NetAmount: 0,
            PurchaseOrderDet_TaxPercent: taxPercent,
            Product_NameEn: productName,
            Unit_NameEn: unitName,
            TaxDetails: taxDetails,
            stockDetails: []
          };
        });
        const calcDetails = [...mappedDetails];
        for (let i = 0; i < calcDetails.length; i++) {
          calculateDetailAmounts(calcDetails, i);
        }
        setDetails(calcDetails);
        recalculateTotalsFromDetails(calcDetails);
      }
      setShowPurchaseOrderModal(false);
    } catch (error) {
      console.error("Error selecting purchase order:", error);
      Swal.fire("Error", "Failed to load purchase order details", "error");
    } finally {
      setLoadingPurchaseOrders(false);
    }
  };

  const handleSave = async () => {
    try {
      if (!isPurchaseAllowed) {
        Swal.fire("Error", "Purchases are not allowed for this branch", "error");
        return;
      }
      if (!form.PurchaseOrder_Business_Id) {
        Swal.fire("Error", "Please select a company", "error");
        return;
      }
      if (!form.PurchaseOrder_Supplier_Id) {
        Swal.fire("Error", "Please select a supplier", "error");
        return;
      }
      if (details.length === 0 || !details.some(detail => detail.PurchaseOrderDet_Product_Id)) {
        Swal.fire("Error", "Please add at least one product", "error");
        return;
      }

      const filteredDetails = details.filter(detail => detail.PurchaseOrderDet_Product_Id);
      if (filteredDetails.length === 0) {
        Swal.fire("Error", "Please add at least one product", "error");
        return;
      }

      const invalidQtyIndex = filteredDetails.findIndex(detail => {
        const qty = parseFloat(detail.PurchaseOrderDet_Quantity);
        return !qty || qty <= 0;
      });
      if (invalidQtyIndex !== -1) {
        Swal.fire({
          icon: 'error',
          title: 'Quantity Required',
          text: `Please enter a quantity greater than 0 for product row ${invalidQtyIndex + 1}.`,
          confirmButtonText: 'OK'
        });
        return;
      }

      const qtyLimitExceededIndex = filteredDetails.findIndex(detail => {
        const qty = parseFloat(detail.PurchaseOrderDet_Quantity) || 0;
        return qty > 300000;
      });
      if (qtyLimitExceededIndex !== -1) {
        Swal.fire({
          icon: 'error',
          title: 'Quantity Limit Exceeded',
          text: `Quantity cannot exceed 300,000. Please adjust the quantity for product row ${qtyLimitExceededIndex + 1}.`,
          confirmButtonText: 'OK'
        });
        return;
      }

      const bonusQtyLimitExceededIndex = filteredDetails.findIndex(detail => {
        const bonusQty = parseFloat(detail.PurchaseOrderDet_BonusQuantity) || 0;
        return bonusQty > 100000;
      });
      if (bonusQtyLimitExceededIndex !== -1) {
        Swal.fire({
          icon: 'error',
          title: 'Bonus Quantity Limit Exceeded',
          text: `Bonus quantity cannot exceed 100,000. Please adjust the bonus quantity for product row ${bonusQtyLimitExceededIndex + 1}.`,
          confirmButtonText: 'OK'
        });
        return;
      }

      for (let i = 0; i < filteredDetails.length; i++) {
        const detail = filteredDetails[i];
        if (!detail.stockDetails || detail.stockDetails.length === 0) {
          Swal.fire({
            icon: 'error',
            title: 'Stock Details Required',
            text: `Please enter stock details for product "${detail.Product_NameEn || 'Unknown'}" before saving.`,
            confirmButtonText: 'OK'
          }).then(() => {
            openStockModal(filteredDetails.findIndex(d => d.PurchaseOrderDet_Product_Id === detail.PurchaseOrderDet_Product_Id));
          });
          return;
        }
        const missingSalesPrice = detail.stockDetails.find((stock) => {
          const salesPrice = parseFloat(stock.PurchaseStock_SalesPrice || stock.purchaseStock_SalesPrice) || 0;
          return salesPrice <= 0;
        });
        if (missingSalesPrice) {
          Swal.fire({
            icon: 'error',
            title: 'Sales Price Required',
            text: `Please enter a sales price greater than 0 for all stock items of product "${detail.Product_NameEn || 'Unknown'}" before saving.`,
            confirmButtonText: 'OK'
          }).then(() => {
            openStockModal(filteredDetails.findIndex(d => d.PurchaseOrderDet_Product_Id === detail.PurchaseOrderDet_Product_Id));
          });
          return;
        }
      }

      if (selectedPurchaseOrderId && !markAsReceived) {
        Swal.fire({
          icon: 'warning',
          title: 'Mark as Received Required',
          text: 'Please check "Mark Purchase Order as Received" checkbox before saving, as this Purchase is based on a Purchase Order.',
          confirmButtonText: 'OK'
        });
        return;
      }

      setSaveLoading(true);
      const session = getUserSession();

      const ensureStockIdsString = (stockIds) => {
        if (!stockIds) return '';
        if (typeof stockIds === 'string') return stockIds;
        if (Array.isArray(stockIds)) return stockIds.join(',');
        return String(stockIds);
      };

      // Prepare stock details for save - format properly with all required fields
      const stockDetailsForSave = filteredDetails
        .filter(detail => detail.PurchaseOrderDet_Product_Id && detail.stockDetails)
        .flatMap(detail => {
          return (detail.stockDetails || []).map(stock => {
            // Calculate cost price from unit price if not provided
            const unitPrice = parseFloat(detail.PurchaseOrderDet_UnitPrice || detail.purchaseOrderDet_UnitPrice) || 0;
            const costPrice = parseFloat(stock.PurchaseStock_CostPrice || stock.purchaseStock_CostPrice) || unitPrice || 0;
            const batchQty = parseFloat(stock.PurchaseStock_BatchQuantity || stock.batchQuantity) || 0;
            const salesPrice = parseFloat(stock.PurchaseStock_SalesPrice || stock.purchaseStock_SalesPrice) || 0;

            // Keep expiry date as string in DD/MM/YYYY format (don't convert to Date object)
            const expDate = stock.PurchaseStock_ExpDate || stock.purchaseStock_ExpDate || null;

            return {
              PurchaseStock_Product_Id: detail.PurchaseOrderDet_Product_Id,
              PurchaseStock_Unit_Id: detail.PurchaseOrderDet_Unit_Id || detail.purchaseOrderDet_Unit_Id || 1,
              PurchaseStock_BatchQuantity: batchQty,
              PurchaseStock_BatchNo: stock.PurchaseStock_BatchNo || stock.purchaseStock_BatchNo || '',
              PurchaseStock_ExpDate: expDate,
              PurchaseStock_SalesPrice: salesPrice,
              PurchaseStock_CostPrice: costPrice,
              PurchaseStock_IsBonus: stock.PurchaseStock_IsBonus || stock.purchaseStock_IsBonus || false,
              StockIds: ensureStockIdsString(stock.StockIds || stock.stockIds || '')
            };
          });
        });

      const totalQty = filteredDetails.reduce((sum, detail) => {
        const quantity = parseFloat(detail.PurchaseOrderDet_Quantity || detail.purchaseOrderDet_Quantity) || 0;
        const bonusQuantity = parseFloat(detail.PurchaseOrderDet_BonusQuantity || detail.purchaseOrderDet_BonusQuantity) || 0;
        return sum + quantity + bonusQuantity;
      }, 0);

      const cleanedDetails = filteredDetails.map(detail => {
        const cleanedDetail = { ...detail };
        if (!cleanedDetail.PurchaseOrderDet_Id || cleanedDetail.PurchaseOrderDet_Id === 0) {
          delete cleanedDetail.PurchaseOrderDet_Id;
        }
        if (cleanedDetail.stockDetails || cleanedDetail.StockDetails) {
          const stockDetailsToFormat = cleanedDetail.stockDetails || cleanedDetail.StockDetails || [];
          cleanedDetail.StockDetails = stockDetailsToFormat.map(stock => {
            const formattedStock = { ...stock };
            if (formattedStock.StockIds !== undefined || formattedStock.stockIds !== undefined) {
              formattedStock.StockIds = ensureStockIdsString(formattedStock.StockIds || formattedStock.stockIds);
              delete formattedStock.stockIds;
            }
            return formattedStock;
          });
          delete cleanedDetail.stockDetails;
        }
        return cleanedDetail;
      });

      const saveData = {
        ...form,
        PurchaseOrder_InvoiceNo: form.PurchaseOrder_InvoiceNo,
        PurchaseOrder_CreatedBy: session.userId,
        PurchaseOrder_TotalQty: totalQty,
        IsReturn: false,
        Return_Purchase_Order_Id: null,
        Details: cleanedDetails,
        StockDetails: stockDetailsForSave,
        TaxDetails: filteredDetails
          .filter(detail => detail.TaxDetails && detail.TaxDetails.length > 0)
          .flatMap((detail, index) => {
            return detail.TaxDetails.map(tax => ({
              TempDetailId: index + 1,
              PurchaseOrderTaxDet_Tax_Id: tax.PurchaseOrderTaxDet_Tax_Id || tax.tax_Id || tax.Tax_Id,
              Tax_NameEn: tax.Tax_NameEn || tax.tax_NameEn,
              Tax_Perc: tax.Tax_Perc || tax.tax_Perc
            }));
          })
      };

      if (!saveData.PurchaseOrder_Id || saveData.PurchaseOrder_Id === 0) {
        delete saveData.PurchaseOrder_Id;
      }

      const result = await savePurchaseOrder(saveData);

      if (result && (result.id > 0 || result.PurchaseOrder_Id > 0)) {
        if (markAsReceived && selectedPurchaseOrderId) {
          try {
            await markPurchaseRequestAsReceived(selectedPurchaseOrderId);
          } catch (error) {
            console.error("Error marking purchase order as received:", error);
          }
        }

        Swal.fire({
          title: "Success",
          text: "Purchase saved successfully!",
          icon: "success",
          confirmButtonText: "OK"
        }).then(() => {
          // Clear the form and stay on the same page
          handleClear();
        });
      } else {
        const errorMessage = result?.message || "Failed to save purchase";
        Swal.fire("Error", errorMessage, "error");
      }
    } catch (error) {
      console.error("Error saving purchase:", error);
      const errorMessage = error?.response?.data?.message ||
        error?.message ||
        "Failed to save purchase";
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleClear = () => {
    const userCompanyId = localStorage.getItem("userCompanyId");
    const userBranchId = localStorage.getItem("userBranchId");
    const resetFormData = {
      ...defaultForm,
      PurchaseOrder_Date: new Date(),
      PurchaseOrder_DueDate: new Date()
    };
    if (userCompanyId && businesses.length > 0) {
      const companyExists = businesses.find(b => b.business_Id === parseInt(userCompanyId));
      if (companyExists) {
        resetFormData.PurchaseOrder_Business_Id = parseInt(userCompanyId);
        if (userBranchId && branches.length > 0) {
          const branchExists = branches.find(b => b.branch_Id === parseInt(userBranchId) && b.branch_Buisness_Id === parseInt(userCompanyId));
          if (branchExists) {
            resetFormData.PurchaseOrder_Branch_Id = parseInt(userBranchId);
          }
        }
      }
    }
    setForm(resetFormData);
    setDetails([{ ...defaultDetail }]);
    setSelectedSupplierOption(null);
    setSupplierSearchOptions([]);
    setFormKey(prev => prev + 1);
    setSelectedPurchaseOrderId(null);
    setSelectedPurchaseOrderData(null);
    setOriginalPurchaseOrderQuantities({});
    setMarkAsReceived(false);
    lastGeneratedBusinessIdRef.current = null;
  };

  const businessOptions = useMemo(() => {
    return businesses.map(business => ({
      value: business.business_Id,
      label: business.business_NameEn
    }));
  }, [businesses]);

  const branchOptions = useMemo(() => {
    return branches
      .filter(branch => branch.branch_Buisness_Id === form.PurchaseOrder_Business_Id)
      .map(branch => ({
        value: branch.branch_Id,
        label: branch.branch_NameEn
      }));
  }, [branches, form.PurchaseOrder_Business_Id]);

  const productOptions = useMemo(() => {
    return products.map(product => ({
      value: product.product_Id,
      label: product.product_NameEn
    }));
  }, [products]);

  const unitOptions = useMemo(() => {
    return units.map(unit => ({
      value: unit.value,
      label: unit.label
    }));
  }, [units]);

  const invoiceTypeOptions = [
    { value: 1, label: "Cash" },
    { value: 2, label: "Credit" }
  ];

  const formatDateDDMMYYYY = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <div className="page-content">
      <style>{`
        input.no-spinner::-webkit-outer-spin-button,
        input.no-spinner::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input.no-spinner[type=number] {
          -moz-appearance: textfield;
        }
      `}</style>
      <Container fluid>
        <Breadcrumb title="Purchase Management" breadcrumbItem="Add Purchase" />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody className="p-4">
                <Form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                  {/* Basic Information */}
                  <Row>
                    <Col md={3}>
                      <div className="mb-3">
                        <Label>Company <span className="text-danger">*</span></Label>
                        <Select
                          key={`business-${formKey}`}
                          value={businessOptions.find(option => option.value === form.PurchaseOrder_Business_Id) || null}
                          onChange={(option) => handleBusinessChange(option?.value || null)}
                          options={businessOptions}
                          placeholder="Select Company"
                          styles={selectStyles}
                          isClearable
                          isSearchable
                          maxMenuHeight={200}
                          isDisabled={!!localStorage.getItem("userCompanyId")}
                        />
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="mb-3">
                        <Label>Branch</Label>
                        <Select
                          key={`branch-${formKey}`}
                          value={branchOptions.find(option => option.value === form.PurchaseOrder_Branch_Id) || null}
                          onChange={(option) => handleFormChange("PurchaseOrder_Branch_Id", option?.value || null)}
                          options={branchOptions}
                          placeholder="Select Branch"
                          styles={selectStyles}
                          isClearable
                          isSearchable
                          maxMenuHeight={200}
                          isDisabled={!form.PurchaseOrder_Business_Id || !!localStorage.getItem("userBranchId")}
                        />
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="mb-3">
                        <Label>Invoice Number</Label>
                        <Input
                          type="text"
                          value={form.PurchaseOrder_InvoiceNo}
                          readOnly
                          style={{ backgroundColor: '#f8f9fa' }}
                        />
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="mb-3">
                        <Label>Date <span className="text-danger">*</span></Label>
                        <DatePicker
                          selected={form.PurchaseOrder_Date}
                          onChange={(date) => handleFormChange("PurchaseOrder_Date", date)}
                          dateFormat="dd/MM/yyyy"
                          className="form-control"
                        />
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={3}>
                      <div className="mb-3">
                        <Label>Due Date</Label>
                        <DatePicker
                          selected={form.PurchaseOrder_DueDate}
                          onChange={(date) => handleFormChange("PurchaseOrder_DueDate", date)}
                          dateFormat="dd/MM/yyyy"
                          className="form-control"
                        />
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="mb-3">
                        <Label>Reference Number</Label>
                        <Input
                          type="text"
                          value={form.PurchaseOrder_RefNo}
                          onChange={(e) => handleFormChange("PurchaseOrder_RefNo", e.target.value)}
                          placeholder="Enter reference number"
                        />
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="mb-3">
                        <Label>Invoice Type <span className="text-danger">*</span></Label>
                        <Select
                          key={`invoiceType-${formKey}`}
                          value={invoiceTypeOptions.find(option => option.value === form.PurchaseOrder_InvoiceType_Id) || null}
                          onChange={(option) => handleFormChange("PurchaseOrder_InvoiceType_Id", option?.value || 1)}
                          options={invoiceTypeOptions}
                          styles={selectStyles}
                        />
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="mb-3">
                        <Label>Supplier <span className="text-danger">*</span></Label>
                        <div className="d-flex gap-2 align-items-end">
                          <div style={{ flex: 1 }}>
                            <Select
                              key={`supplier-${formKey}`}
                              value={selectedSupplierOption}
                              onChange={async (option) => {
                                setSelectedSupplierOption(option);
                                const supplierId = option?.value || null;
                                handleFormChange("PurchaseOrder_Supplier_Id", supplierId);
                                if (supplierId) {
                                  await loadPurchaseOrdersForSupplier(supplierId);
                                } else {
                                  setSelectedPurchaseOrderId(null);
                                  setSelectedPurchaseOrderData(null);
                                  setOriginalPurchaseOrderQuantities({});
                                  setMarkAsReceived(false);
                                }
                              }}
                              options={supplierSearchOptions}
                              placeholder="Search by name..."
                              styles={selectStyles}
                              isClearable
                              isSearchable
                              maxMenuHeight={200}
                              isLoading={supplierSearchLoading}
                              noOptionsMessage={() => "Type at least 3 characters"}
                              loadingMessage={() => "Searching..."}
                              filterOption={null}
                              onInputChange={(inputValue) => {
                                if (supplierSearchTimeout) {
                                  clearTimeout(supplierSearchTimeout);
                                }
                                const trimmedValue = inputValue ? inputValue.trim() : '';
                                const timeout = setTimeout(() => {
                                  if (trimmedValue.length >= 3) {
                                    searchSuppliersManually(trimmedValue);
                                  } else {
                                    setSupplierSearchOptions([]);
                                  }
                                }, 300);
                                setSupplierSearchTimeout(timeout);
                                return inputValue;
                              }}
                            />
                          </div>
                          {form.PurchaseOrder_Supplier_Id && (
                            <Button
                              type="button"
                              color="info"
                              size="sm"
                              onClick={async () => {
                                try {
                                  setLoadingPurchaseOrders(true);
                                  const filter = {
                                    PurchaseRequest_Supplier_Id: form.PurchaseOrder_Supplier_Id,
                                    PurchaseRequest_Is_Received: false
                                  };
                                  const data = await getPurchaseRequests(filter);
                                  if (data && data.length > 0) {
                                    setPurchaseOrdersList(data || []);
                                    setShowPurchaseOrderModal(true);
                                  } else {
                                    Swal.fire({
                                      icon: 'info',
                                      title: 'No Purchase Orders',
                                      text: 'There are no unreceived Purchase Orders for this supplier.',
                                      confirmButtonText: 'OK'
                                    });
                                  }
                                } catch (error) {
                                  console.error("Error loading purchase orders:", error);
                                  Swal.fire({
                                    icon: 'error',
                                    title: 'Error',
                                    text: 'Failed to load Purchase Orders.',
                                    confirmButtonText: 'OK'
                                  });
                                } finally {
                                  setLoadingPurchaseOrders(false);
                                }
                              }}
                              title="Select Purchase Order"
                              style={{ minWidth: '40px', height: '38px' }}
                            >
                              <i className="bx bx-list-ul"></i>
                            </Button>
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>

                  {/* Details Section */}
                  <Row>
                    <Col md={12}>
                      <Label className="fw-bold mb-3">Purchase Details</Label>
                      <div className="table-responsive">
                        <Table bordered style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                          <thead className="table-light">
                            <tr>
                              <th width="5%" style={{ padding: '8px 12px', border: '1px solid #dee2e6', textAlign: 'center' }}>Sr.No</th>
                              <th width="20%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Product</th>
                              <th width="10%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Unit</th>
                              <th width="8%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Qty</th>
                              <th width="8%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Bonus Qty</th>
                              <th width="10%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Unit Price</th>
                              <th width="6%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Discount %</th>
                              <th width="6%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Tax %</th>
                              <th width="12%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Total Amount</th>
                              <th width="12%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Net Amount</th>
                              <th width="5%" style={{ padding: '8px 12px', border: '1px solid #dee2e6', textAlign: 'center' }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {details
                              .filter(detail => detail && getDetailField(detail, 'PurchaseOrderDet_Quantity') !== undefined)
                              .map((detail, index) => {
                                return (
                                  <tr key={index}>
                                    <td style={{ padding: '0', textAlign: 'center', border: '1px solid #dee2e6' }}>{index + 1}</td>
                                    <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                      <Select
                                        key={`product-${formKey}-${index}`}
                                        value={productOptions.find(option => option.value === getDetailField(detail, 'PurchaseOrderDet_Product_Id')) || null}
                                        onChange={(option) => handleDetailChange(index, 'PurchaseOrderDet_Product_Id', option?.value || null)}
                                        options={productOptions}
                                        placeholder="Select Product"
                                        styles={{
                                          ...selectStyles,
                                          control: (provided) => ({
                                            ...provided,
                                            border: 'none',
                                            borderRadius: '0',
                                            minHeight: '38px',
                                            boxShadow: 'none'
                                          })
                                        }}
                                        isClearable
                                        isSearchable
                                        maxMenuHeight={200}
                                      />
                                    </td>
                                    <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                      <Input
                                        type="text"
                                        value={getDetailField(detail, 'Unit_NameEn')}
                                        readOnly
                                        style={{
                                          backgroundColor: '#f8f9fa',
                                          border: 'none',
                                          borderRadius: '0',
                                          padding: '8px 12px'
                                        }}
                                        placeholder="Select Product"
                                      />
                                    </td>
                                    <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                      <Input
                                        type="number"
                                        min="0"
                                        max="300000"
                                        step="1"
                                        value={formatNumericValue(getDetailField(detail, 'PurchaseOrderDet_Quantity'))}
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          if (value === '' || /^\d+$/.test(value)) {
                                            const numValue = value === '' ? 0 : parseInt(value, 10) || 0;
                                            if (numValue > 300000) {
                                              Swal.fire({
                                                icon: 'error',
                                                title: 'Quantity Limit Exceeded',
                                                text: 'Quantity cannot exceed 300,000. Please enter a value within the limit.',
                                                confirmButtonText: 'OK'
                                              });
                                              return;
                                            }
                                            handleDetailChange(index, 'PurchaseOrderDet_Quantity', numValue);
                                          }
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === '.' || e.key === ',' || e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                                            e.preventDefault();
                                          }
                                        }}
                                        className="no-spinner"
                                        style={{
                                          border: 'none',
                                          borderRadius: '0',
                                          padding: '8px 12px',
                                          appearance: 'none',
                                          MozAppearance: 'textfield'
                                        }}
                                      />
                                    </td>
                                    <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                      <Input
                                        type="number"
                                        min="0"
                                        max="100000"
                                        step="1"
                                        value={formatNumericValue(getDetailField(detail, 'PurchaseOrderDet_BonusQuantity'))}
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          if (value === '' || /^\d+$/.test(value)) {
                                            const numValue = value === '' ? 0 : parseInt(value, 10) || 0;
                                            if (numValue > 100000) {
                                              Swal.fire({
                                                icon: 'error',
                                                title: 'Bonus Quantity Limit Exceeded',
                                                text: 'Bonus quantity cannot exceed 100,000. Please enter a value within the limit.',
                                                confirmButtonText: 'OK'
                                              });
                                              return;
                                            }
                                            handleDetailChange(index, 'PurchaseOrderDet_BonusQuantity', numValue);
                                          }
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === '.' || e.key === ',' || e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                                            e.preventDefault();
                                          }
                                        }}
                                        className="no-spinner"
                                        style={{
                                          border: 'none',
                                          borderRadius: '0',
                                          padding: '8px 12px',
                                          appearance: 'none',
                                          MozAppearance: 'textfield'
                                        }}
                                      />
                                    </td>
                                    <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                      <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={formatNumericValue(getDetailField(detail, 'PurchaseOrderDet_UnitPrice'))}
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          handleDetailChange(index, 'PurchaseOrderDet_UnitPrice', value === '' ? 0 : parseFloat(value) || 0);
                                        }}
                                        className="no-spinner"
                                        style={{
                                          border: 'none',
                                          borderRadius: '0',
                                          padding: '8px 12px',
                                          appearance: 'none',
                                          MozAppearance: 'textfield'
                                        }}
                                      />
                                    </td>
                                    <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                      <Input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.01"
                                        value={formatNumericValue(getDetailField(detail, 'PurchaseOrderDet_DiscountPercent'))}
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          handleDetailChange(index, 'PurchaseOrderDet_DiscountPercent', value === '' ? 0 : parseFloat(value) || 0);
                                        }}
                                        className="no-spinner"
                                        style={{
                                          border: 'none',
                                          borderRadius: '0',
                                          padding: '8px 12px',
                                          appearance: 'none',
                                          MozAppearance: 'textfield'
                                        }}
                                      />
                                    </td>
                                    <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                      <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={getDetailField(detail, 'PurchaseOrderDet_TaxPercent')}
                                        readOnly
                                        style={{
                                          backgroundColor: '#f8f9fa',
                                          border: 'none',
                                          borderRadius: '0',
                                          padding: '8px 12px'
                                        }}
                                      />
                                    </td>
                                    <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                      <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={getDetailField(detail, 'PurchaseOrderDet_TotalAmount') || getDetailField(detail, 'purchaseOrderDet_TotalAmount') || 0}
                                        readOnly
                                        style={{
                                          backgroundColor: '#f8f9fa',
                                          border: 'none',
                                          borderRadius: '0',
                                          padding: '8px 12px'
                                        }}
                                      />
                                    </td>
                                    <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                      <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={getDetailField(detail, 'PurchaseOrderDet_NetAmount')}
                                        readOnly
                                        style={{
                                          backgroundColor: '#f8f9fa',
                                          border: 'none',
                                          borderRadius: '0',
                                          padding: '8px 12px'
                                        }}
                                      />
                                    </td>
                                    <td style={{ padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', width: '5%' }}>
                                      <div className="d-flex justify-content-center align-items-center gap-1" style={{ minHeight: '38px' }}>
                                        <Button
                                          type="button"
                                          color="info"
                                          size="sm"
                                          onClick={() => openStockModal(index)}
                                          disabled={!getDetailField(detail, 'PurchaseOrderDet_Product_Id')}
                                          style={{
                                            padding: '6px 10px',
                                            minWidth: '36px',
                                            height: '32px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: '4px'
                                          }}
                                          title="Stock Details"
                                        >
                                          <i className="bx bx-box" style={{ fontSize: '16px' }}></i>
                                        </Button>
                                        {details.length > 1 && (
                                          <Button
                                            type="button"
                                            color="danger"
                                            size="sm"
                                            onClick={() => removeDetail(index)}
                                            style={{
                                              padding: '6px 10px',
                                              minWidth: '36px',
                                              height: '32px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              borderRadius: '4px'
                                            }}
                                            title="Remove Row"
                                          >
                                            <Minus size={14} />
                                          </Button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </Table>
                      </div>
                      <Button
                        type="button"
                        color="success"
                        size="sm"
                        onClick={addDetail}
                        className="mt-2"
                      >
                        <Plus size={16} /> Add Row
                      </Button>
                    </Col>
                  </Row>

                  {/* Summary Section */}
                  <Row className="mt-4">
                    <Col md={6}>
                      <div className="mb-3">
                        <Label>Notes</Label>
                        <Input
                          type="textarea"
                          rows="3"
                          value={form.PurchaseOrder_Notes}
                          onChange={(e) => handleFormChange("PurchaseOrder_Notes", e.target.value)}
                          placeholder="Enter any notes"
                        />
                      </div>
                    </Col>
                    {selectedPurchaseOrderId && (
                      <Col md={6}>
                        <div className="mb-3">
                          <div className="form-check mt-4">
                            <Input
                              type="checkbox"
                              className="form-check-input"
                              id="markAsReceived"
                              checked={markAsReceived}
                              onChange={(e) => setMarkAsReceived(e.target.checked)}
                            />
                            <Label className="form-check-label" for="markAsReceived">
                              Mark Purchase Order as Received
                            </Label>
                          </div>
                        </div>
                      </Col>
                    )}
                    <Col md={12}>
                      <Row>
                        <Col md={4}>
                          <div className="mb-3">
                            <Label>Total Amount</Label>
                            <Input
                              type="number"
                              value={form.PurchaseOrder_TotalAmount}
                              readOnly
                              style={{ backgroundColor: '#f8f9fa' }}
                            />
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="mb-3">
                            <Label>Total Discount Amount</Label>
                            <Input
                              type="number"
                              value={form.PurchaseOrder_DiscountAmount}
                              readOnly
                              style={{ backgroundColor: '#f8f9fa' }}
                            />
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="mb-3">
                            <Label>Total Tax Amount</Label>
                            <Input
                              type="number"
                              value={form.PurchaseOrder_TaxAmount}
                              readOnly
                              style={{ backgroundColor: '#f8f9fa' }}
                            />
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={4}>
                          <div className="mb-3">
                            <Label>Other Charges</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={formatNumericValue(form.PurchaseOrder_OtherCharges)}
                              onChange={(e) => {
                                const value = e.target.value;
                                handleFormChange("PurchaseOrder_OtherCharges", value === '' ? 0 : parseFloat(value) || 0);
                              }}
                            />
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="mb-3">
                            <Label>Additional Discount</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={formatNumericValue(form.PurchaseOrder_AdditionalDiscount)}
                              onChange={(e) => {
                                const value = e.target.value;
                                handleFormChange("PurchaseOrder_AdditionalDiscount", value === '' ? 0 : parseFloat(value) || 0);
                              }}
                            />
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="mb-3">
                            <Label className="fw-bold">Net Amount</Label>
                            <Input
                              type="number"
                              value={form.PurchaseOrder_NetAmount}
                              readOnly
                              style={{ backgroundColor: '#e3f2fd', fontWeight: 'bold' }}
                            />
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  </Row>

                  <Row className="mt-4">
                    <Col>
                      <div className="d-flex gap-2">
                        {isPurchaseAllowed && (
                          <Button
                            type="submit"
                            color="primary"
                            disabled={saveLoading}
                            className="waves-effect waves-light"
                          >
                            {saveLoading && (
                              <ClipLoader size={16} color="#ffffff" className="me-2" />
                            )}
                            Save Purchase
                          </Button>
                        )}
                        <Button
                          type="button"
                          color="secondary"
                          onClick={handleClear}
                          className="waves-effect waves-light"
                        >
                          Clear
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Stock Details Modal */}
      <Modal isOpen={showStockModal} toggle={handleStockModalClose} size="xl">
        <style>{`
          .stock-details-modal-content {
            font-size: 0.8125rem;
          }
          .stock-details-modal-content .table {
            font-size: 0.8125rem;
          }
          .stock-details-modal-content .table th,
          .stock-details-modal-content .table td {
            font-size: 0.8125rem;
            padding: 0.4rem 0.5rem;
          }
          .stock-details-modal-content .form-control {
            font-size: 0.8125rem;
            padding: 0.375rem 0.5rem;
          }
          .stock-details-modal-content .badge {
            font-size: 0.6875rem;
            padding: 0.25rem 0.5rem;
          }
          .stock-details-modal-header {
            font-size: 0.9375rem;
          }
          .stock-details-modal-header .modal-title {
            font-size: 0.9375rem;
          }
        `}</style>
        <ModalHeader toggle={handleStockModalClose} className="stock-details-modal-header">
          Stock Details - {currentProduct?.product_NameEn}
          {previousPrices?.hasPreviousPrices && previousPrices?.previousSalesPrice && (
            <span className="ms-3 text-muted">
              (Last Sales Price: {parseFloat(previousPrices.previousSalesPrice).toFixed(2)})
            </span>
          )}
        </ModalHeader>
        <ModalBody className="stock-details-modal-content">
          {(() => {
            const detail = details[currentDetailIndex];
            if (!detail) return null;

            const quantity = parseFloat(detail.purchaseOrderDet_Quantity || detail.PurchaseOrderDet_Quantity) || 0;
            const bonusQuantity = parseFloat(detail.purchaseOrderDet_BonusQuantity || detail.PurchaseOrderDet_BonusQuantity) || 0;
            const totalQuantity = quantity + bonusQuantity;

            const allocatedQuantity = stockDetails.reduce((sum, batch) => sum + (parseFloat(batch.batchQuantity) || 0), 0);
            const remainingQuantity = totalQuantity - allocatedQuantity;

            const regularAllocated = stockDetails
              .filter(batch => !(batch.PurchaseStock_IsBonus || batch.purchaseStock_IsBonus))
              .reduce((sum, batch) => sum + (parseFloat(batch.batchQuantity) || 0), 0);
            const bonusAllocated = stockDetails
              .filter(batch => (batch.PurchaseStock_IsBonus || batch.purchaseStock_IsBonus))
              .reduce((sum, batch) => sum + (parseFloat(batch.batchQuantity) || 0), 0);

            const regularRemaining = quantity - regularAllocated;
            const bonusRemaining = bonusQuantity - bonusAllocated;

            return (
              <div>
                <div className="mb-3 p-2 bg-light rounded">
                  <strong>Regular Quantity:</strong> {quantity} |
                  <strong className="ms-2">Allocated:</strong> {regularAllocated} |
                  <strong className="ms-2">Remaining:</strong> <span className={regularRemaining < 0 ? 'text-danger' : regularRemaining > 0 ? 'text-warning' : 'text-success'}>{regularRemaining}</span>
                  <br />
                  <strong>Bonus Quantity:</strong> {bonusQuantity} |
                  <strong className="ms-2">Allocated:</strong> {bonusAllocated} |
                  <strong className="ms-2">Remaining:</strong> <span className={bonusRemaining < 0 ? 'text-danger' : bonusRemaining > 0 ? 'text-warning' : 'text-success'}>{bonusRemaining}</span>
                </div>
                <div className="table-responsive">
                  <Table bordered>
                    <thead className="table-light">
                      <tr>
                        <th width="5%">Sr.No</th>
                        <th width="12%">Quantity</th>
                        <th width="15%">Batch Number</th>
                        <th width="15%">Expiry Date</th>
                        <th width="15%">Sales Price</th>
                        <th width="15%">Cost Price</th>
                        <th width="10%">Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stockDetails.map((stock, index) => {
                        return (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td>
                              <Input
                                type="number"
                                min="0"
                                step="1"
                                value={stock.batchQuantity || 0}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  handleStockChange(index, 'batchQuantity', value === '' ? 0 : parseFloat(value) || 0);
                                }}
                                placeholder="Qty"
                              />
                            </td>
                            <td>
                              <Input
                                type="text"
                                value={stock.PurchaseStock_BatchNo || stock.purchaseStock_BatchNo || ''}
                                onChange={(e) => handleStockChange(index, 'PurchaseStock_BatchNo', e.target.value)}
                                placeholder="Enter batch number"
                              />
                            </td>
                            <td>
                              {currentProduct?.expiryRequired ? (
                                <Input
                                  type="text"
                                  value={stock.PurchaseStock_ExpDate || stock.purchaseStock_ExpDate || ''}
                                  onChange={(e) => {
                                    const dateStr = e.target.value;
                                    handleStockChange(index, 'PurchaseStock_ExpDate', dateStr);
                                  }}
                                  placeholder="DD/MM/YYYY"
                                  maxLength="10"
                                />
                              ) : (
                                <Input
                                  type="text"
                                  value="N/A"
                                  readOnly
                                  style={{ backgroundColor: '#f8f9fa' }}
                                />
                              )}
                            </td>
                            <td>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={formatNumericValue(stock.PurchaseStock_SalesPrice || stock.purchaseStock_SalesPrice || 0)}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  handleStockChange(index, 'PurchaseStock_SalesPrice', value === '' ? 0 : parseFloat(value) || 0);
                                }}
                                placeholder="Sales price"
                              />
                            </td>
                            <td>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={stock.purchaseStock_CostPrice || stock.PurchaseStock_CostPrice || (() => {
                                  const detail = details[currentDetailIndex];
                                  if (!detail) return 0;
                                  const quantity = parseFloat(detail.purchaseOrderDet_Quantity || detail.PurchaseOrderDet_Quantity) || 0;
                                  const bonusQuantity = parseFloat(detail.purchaseOrderDet_BonusQuantity || detail.PurchaseOrderDet_BonusQuantity) || 0;
                                  const totalQuantity = quantity + bonusQuantity;
                                  const unitPrice = parseFloat(detail.purchaseOrderDet_UnitPrice || detail.PurchaseOrderDet_UnitPrice) || 0;
                                  const totalAmount = parseFloat(quantity * unitPrice) || 0;
                                  return totalQuantity > 0 ? (totalAmount / totalQuantity).toFixed(2) : 0;
                                })()}
                                readOnly
                                style={{ backgroundColor: '#f8f9fa' }}
                              />
                            </td>
                            <td>
                              <span className={`badge ${(stock.purchaseStock_IsBonus || stock.PurchaseStock_IsBonus) ? 'bg-warning' : 'bg-primary'}`}>
                                {(stock.purchaseStock_IsBonus || stock.PurchaseStock_IsBonus) ? 'Bonus' : 'Regular'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
                <div className="mt-3 d-flex gap-2">
                  {regularRemaining > 0 && (
                    <Button color="success" onClick={() => addNextBatch(false)}>
                      <Plus size={16} className="me-1" />
                      Add Regular Batch ({regularRemaining} remaining)
                    </Button>
                  )}
                  {bonusRemaining > 0 && (
                    <Button color="warning" onClick={() => addNextBatch(true)}>
                      <Plus size={16} className="me-1" />
                      Add Bonus Batch ({bonusRemaining} remaining)
                    </Button>
                  )}
                </div>
              </div>
            );
          })()}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={saveStockDetails}>
            Save Stock Details
          </Button>
          <Button color="secondary" onClick={handleStockModalClose}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Purchase Order Selection Modal */}
      <Modal isOpen={showPurchaseOrderModal} toggle={() => setShowPurchaseOrderModal(false)} size="lg">
        <ModalHeader toggle={() => setShowPurchaseOrderModal(false)}>
          Select Purchase Order
        </ModalHeader>
        <ModalBody>
          {loadingPurchaseOrders ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading Purchase Orders...</p>
            </div>
          ) : purchaseOrdersList.length > 0 ? (
            <div className="table-responsive">
              <Table bordered hover>
                <thead className="table-light">
                  <tr>
                    <th>Order No</th>
                    <th>Date</th>
                    <th>Net Amount</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {purchaseOrdersList.map((po) => {
                    const poId = po.purchaseRequest_Id || po.PurchaseRequest_Id;
                    const orderNo = po.purchaseRequest_OrderNo || po.PurchaseRequest_OrderNo;
                    const poDate = po.purchaseRequest_Date || po.PurchaseRequest_Date;
                    const netAmount = po.purchaseRequest_NetAmount || po.PurchaseRequest_NetAmount || 0;
                    return (
                      <tr key={poId}>
                        <td>{orderNo || 'N/A'}</td>
                        <td>{poDate ? formatDateDDMMYYYY(poDate) : 'N/A'}</td>
                        <td>{Number(netAmount).toFixed(2)}</td>
                        <td>
                          <Button
                            color="primary"
                            size="sm"
                            onClick={() => handlePurchaseOrderSelect(po)}
                          >
                            Select
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-4">
              <p>No unreceived Purchase Orders found for this supplier.</p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowPurchaseOrderModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}